import { Address, RoundingMethod, TaxAggMethod } from "~/entities/siteEntities";
import {
  BundleComponent,
  DiscountType,
  Promotion,
} from "~/entities/promotionEntities";
import { FileReference } from "~/entities/baseEntities";
import { Product } from "~/entities/productEntities";
import { TaxInfo } from "~/entities/taxEntities";
import { v4 } from "uuid";

export enum OrderSource {
  ONLINE = "ONLINE",
  POS = "POS",
  KIOSK = "KIOSK",
  MOBILE = "MOBILE",
  TABLE_ORDERING = "TABLE_ORDERING",
  // Ordering ahead for events: its own order types, menus and lead times, at /catering
  CATERING = "CATERING",
}

export enum GlobalOrderTypeCode {
  POS_IN_STORE = "POS_IN_STORE",
  POS_COLLECTION = "POS_COLLECTION",
  POS_DELIVERY = "POS_DELIVERY",
  TABLE_ORDERING_IN_STORE = "TABLE_ORDERING_IN_STORE",
  ONLINE_COLLECTION = "ONLINE_COLLECTION",
}

export enum OrderStatus {
  EMPTY = "EMPTY",
  DRAFT = "DRAFT",
  HELD = "HELD",
  SENT = "SENT",
  VOIDED = "VOIDED",
  CANCELLED = "CANCELLED",
}

export enum OrderFulfillmentType {
  IN_STORE = "IN_STORE", // e.g. dine in
  COLLECTION = "COLLECTION", // e.g. pickup or takeaway
  DELIVERY = "DELIVERY",
}

export interface OrderType {
  id: string;
  code: string;
  name: string;
  active: boolean;
  source: OrderSource;
  fulfillmentType: OrderFulfillmentType;
  // Who delivers a delivery order type, e.g. "UBER_DIRECT" (sent by the API; absent on older responses)
  deliveryService?: string | null;
}

// REQUEST STRUCTURES

export interface RequestOrder {
  tableNumber: string | null;
  orderTypeCode: string;
  requestedReadyTime: string | null;
  customer: Customer;
  redemptionKey: string | null;
  promoCode: string | null;
  groupOrder: boolean;
  deliveryInfo: DeliveryInfo | null;
}

export interface DeliveryInfo {
  address: Address;
}

export interface PlaceOrderRequest extends RequestOrder {
  lines: RequestLine[];
  isLogout: boolean;
}

export interface Customer {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface BaseOrderLine {
  id: string;
  menuCode: string;
  menuItemCode: string | null;
}
export interface RequestLine extends BaseOrderLine {
  item: RequestLineItem;
}

export interface BaseLineItem {
  id: string;
  qty: number;
  productCode: string;
  sku: string;
}
export interface RequestLineItem extends BaseLineItem {
  children: RequestLineItem[];
}

// RESPONSE STRUCTURES
export interface ResponseOrder extends RequestOrder {
  id: string;
  taxAggregationMethod: TaxAggMethod;
  taxRoundingMethod: RoundingMethod;
  orderNumber: string;
  pricesIncludeTax: boolean;
  lineTotalBeforeDiscount: number;
  totalBeforeDiscount: number;
  finalPriceExTax: number;
  finalPriceIncTax: number;
  discounts: Discount[];
  additionalCharges: Charge[];
  promotionSchedulingOverride: Record<string, boolean>;
  status: OrderStatus;
  estimatedReadyTime: string | null;

  // Not in db object
  paidAmount: number;
  pendingPaymentIds: string[];
  unlockedPromotions: Promotion[];
  redemptionName: string | null;
}
export interface ResponseOrderWithLines extends ResponseOrder {
  lines: ResponseLine[];
  customerId: string | null;
}

export interface ResponseOrderWithSimpleOrderLines extends ResponseOrder {
  orgId: string;
  siteId: string;
  siteName: string;
  paidTime: string | null;
  orderLines: SimpleOrderLine[];
}

export interface SimpleOrderLine {
  id: string;
  item: SimpleItem;
  menuCode: string;
}

export interface SimpleItem {
  name: string;
  qty: number;
  price: number;
  hide: boolean;
  children: SimpleItem[];
}

export interface ResponseLine extends BaseOrderLine {
  item: ResponseLineItem;
  discounts: Discount[];
}

export interface BaseResponseLineItem extends BaseLineItem {
  displayName: string;
  unitPrice: number;
  finalUnitPrice: number;
  hide: boolean;
  discounts: Discount[];
}
export interface ResponseLineItem extends BaseResponseLineItem {
  children: ResponseLineItem[];
}

export interface Discount {
  id: string;
  name: string;
  promotionCode: string;
  percentTimes1000: number;
  fixedAmount: number;
  // What the discount saved, from the server. Absent on discounts added optimistically while saving.
  amount?: number;
}

export interface Charge {
  amount: number;
  finalAmount: number;
  name: string;
  taxes: TaxInfo[];
}

export interface ResponsePromotion {
  code: string;
  name: string;

  // Promotion requirements (see also schedules)
  orderTypeCodes: string[];
  requiredProductCodes: string[];
  requiredQty: number;
  requiredPrice: number;

  // Promotion reward specifications
  discountType: DiscountType;
  productCodes: string[];
  fixedAmount: number;
  percentTimes1000: number;
  maxUsesPerOrder: null | number;
  maxUsesPerRequirements: null | number;
  menuCodes?: string[];

  // For showing deals on the menu
  description?: string;
  image?: FileReference | null;

  // BUNDLE_PRICE only
  bundleComponents?: BundleComponent[] | null;
  targetPrice?: number | null;
}

// CART STRUCTURES
export interface CartLine extends BaseOrderLine {
  item: CartLineItem;
  discounts: Discount[];
}
export interface CartLineItem extends BaseResponseLineItem {
  children: CartLineItem[];
  product: Product;
}

export interface ResponseOrderWithCartLines extends ResponseOrder {
  lines: CartLine[];
}

// FUNCTIONS

export const cartToReq = (
  x: ResponseOrderWithCartLines,
  isLogout?: boolean,
): PlaceOrderRequest => ({
  tableNumber: x.tableNumber,
  orderTypeCode: x.orderTypeCode,
  requestedReadyTime: x.requestedReadyTime,
  customer: x.customer,
  redemptionKey: x.redemptionKey,
  promoCode: x.promoCode,
  groupOrder: x.groupOrder,
  lines: x.lines.map((l) => ({ ...l, item: cartToReqItem(l.item) })),
  isLogout: !!isLogout,
  deliveryInfo: x.deliveryInfo,
});

const cartToReqItem = (x: CartLineItem): RequestLineItem => ({
  id: x.id,
  qty: x.qty,
  productCode: x.productCode,
  sku: x.sku,
  children: x.children.map((c) => cartToReqItem(c)),
});

export const duplicateLineWithoutDiscounts = (line: CartLine): CartLine => ({
  ...line,
  id: v4(),
  discounts: [],
  item: duplicateItemWithoutDiscounts(line.item),
});

export const duplicateItemWithoutDiscounts = (
  item: CartLineItem,
): CartLineItem => ({
  ...item,
  id: v4(),
  discounts: [],
  children: item.children.map((x) => duplicateItemWithoutDiscounts(x)),
});
